import { gql, useQuery } from '@apollo/client'
import {
  BodyText,
  Caption,
  ChevronRightIcon,
  Heading,
  Link,
  Separator,
  Spinner,
  TruncateText,
} from '@farol-ds/react'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  COMPONENT_NAME,
  FEATURE_CLASS,
  FEATURE_NAME,
  GA4ClickEvent,
  GA4CustomComponentRenderedEvent,
  GA4FeatureUseEvent,
  REQUIRED_USER_STATUS,
  SELECTED_OPTION,
} from '@jusbrasil-web/shared-ga4-events'
import { hotjarEvent } from '@jusbrasil-web/shared-hotjar-script'
import { ListBlock } from '@jusbrasil-web/shared-list-block'
import { Image } from '@jusbrasil-web/shared-next-image'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'

import { RELATED_LEGIS_COMPONENT_HEADING_COPY } from '../../../constants'
import { transformRelatedLegisTitle } from '../../../functions'
import { useMetrics } from '../../../hooks'
import { EntityType } from '../config'
import { useDoctrineData } from '../hooks/use-doctrine-data'
import { useTopicData } from '../hooks/use-topic-data'
import {
  DocPopoverTopicByUrlQuery,
  DocPopoverTopicQuery,
} from './__generated__/doc-popover-topic.graphql'
import styles from './styles.module.scss'

export type DocPopoverTopicProps = {
  query: DocPopoverTopicQuery
  entityType: EntityType
  entityUrl: string
  context: string
  hasLRSubscription?: boolean
}

function checkForCommonTopicType(topics: DocPopoverTopicQuery['root']['topics']) {
  if (!topics) return null
  const topicTypes = topics.map((topic) => topic.topicType)
  const commonType = topicTypes.reduce((acc, type) => {
    if (acc === type) return acc
    return null
  })
  return commonType
}

const formatDescription = (description: string) => {
  const itemStartRegex = /^(Art\. \d+|[IVXLCDM]+|[a-z]\))/
  return description.replace(itemStartRegex, (match) => `<strong>${match}</strong>`)
}
export function DocPopoverTopic(props: DocPopoverTopicProps) {
  const {
    query: {
      root: { topics },
    },
    entityType,
    entityUrl,
    context,
    hasLRSubscription,
  } = props

  const { data: dataFallbackQuery, loading: loadingFallbackQuery } =
    useQuery<DocPopoverTopicByUrlQuery>(DocPopoverTopic.fallbackQuery, {
      skip: topics?.length > 0,
      variables: {
        urls: [entityUrl],
      },
    })

  const topicDescriptionRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (dataFallbackQuery) {
      setCurrentTopicIdx(
        dataFallbackQuery?.root?.topicsByUrls && dataFallbackQuery?.root?.topicsByUrls?.length === 1
          ? 0
          : null
      )
    }
  }, [dataFallbackQuery])

  // const shouldRenderDefaultLayout = experimentAlternative === 'control' || !isLogged

  const isInTopicContext = context === 'TOPIC'
  const sourceComponent = isInTopicContext ? 'RelatedLegis' : ''
  const currentTopics =
    topics?.length > 0
      ? topics
      : (dataFallbackQuery?.root.topicsByUrls as DocPopoverTopicByUrlQuery['root']['topicsByUrls'])

  const [currentTopicIdx, setCurrentTopicIdx] = useState<number | null>(
    currentTopics && currentTopics?.length === 1 ? 0 : null
  )

  // For the experiment, we show only federal topics when are multiple topics and only 1 is federal:
  const federalIndexes = currentTopics
    .map((topic, index: number) =>
      (!topic.state || topic.state === '0') && !topic.county ? index : -1
    )
    .filter((index: number) => index !== -1)

  useEffect(() => {
    if (currentTopics?.length > 1 && federalIndexes.length === 1) {
      setCurrentTopicIdx(federalIndexes[0])
    }
  }, [currentTopics, federalIndexes])

  const selectedTopic = currentTopicIdx === null ? null : currentTopics[currentTopicIdx]
  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()
  const topic = useTopicData({
    topic: selectedTopic,
    context: selectedTopic?.lawItemType === 'EMENTA' ? 'EMENTA' : 'TOPIC',
  })
  const doctrine = useDoctrineData({
    title: 'Doutrina sobre este ato normativo',
    topic: selectedTopic,
  })

  // caught by the error boundary
  if (selectedTopic && !topic && !doctrine) {
    throw new Error('Topic not found')
  }

  const defaultProps = useMemo(() => {
    const resolvedTopicType = topic?.topicType ?? checkForCommonTopicType(currentTopics)
    return {
      from_component: 'TopicPopover',
      source_component: sourceComponent,
      topic_type: resolvedTopicType,
      metadata: {
        topicId: topic?.topicId,
        topicType: resolvedTopicType,
      },
    }
  }, [topic, currentTopics, sourceComponent])

  const isLawTopic = topic?.type === 'legislation'

  const { sendGA4Event } = useGA4Dispatcher()
  const ga4Props = {
    componentClass: COMPONENT_CLASS.RELATED_DOCUMENT_POPOVER,
    componentName: COMPONENT_NAME.RELATED_LAW,
  }

  useEffect(() => {
    if (isLawTopic) {
      sendGA4Event(
        new GA4CustomComponentRenderedEvent({
          ...ga4Props,
          documentId: `${topic.topicType}:${topic.topicId}`,
        })
      )
    }

    if (document.visibilityState === 'visible') {
      const eventName = isInTopicContext ? 'Topic.PopoverOpened' : 'Doc.PopoverOpened'
      sendMetrics(eventName, defaultProps)
    }
    if (defaultProps.topic_type === 'ATO_NORMATIVO' && isInTopicContext) {
      hotjarEvent('event', 'legis-survey-v1')
    }
    // eslint-disable-next-line
  }, [sendMetrics, defaultProps])

  useEffect(() => {
    if (topicDescriptionRef.current) {
      topicDescriptionRef.current.scrollIntoView({ behavior: 'instant', block: 'start' })
    }
  }, [topic?.description])

  if (currentTopics?.length > 1 && !selectedTopic) {
    return (
      <div className={styles.contentRootSlim} ref={dispatcherRef}>
        <Heading size="sm">Este termo refere-se a:</Heading>

        <ListBlock>
          {currentTopics.map((topic, idx) => (
            <ListBlock.Item key={idx}>
              <ListBlock.Content asChild>
                <button onClick={() => setCurrentTopicIdx(idx)}>
                  <ListBlock.Text>{topic.title}</ListBlock.Text>
                  <ListBlock.Trailing>
                    <ChevronRightIcon />
                  </ListBlock.Trailing>
                </button>
              </ListBlock.Content>
            </ListBlock.Item>
          ))}
        </ListBlock>
      </div>
    )
  }

  const trackTopicLinkClick = () => {
    sendGA4Event(
      new GA4ClickEvent({
        ...ga4Props,
        selectedOption: SELECTED_OPTION.FULL_DOCUMENT_ACCESS,
        resourceId: `${topic?.topicType}:${topic?.topicId}`,
      })
    )
    const eventName = isInTopicContext ? 'Topic.PopoverClicked' : 'Doc.PopoverClicked'
    sendMetrics(eventName, defaultProps)
  }

  const onCopy = () => {
    sendGA4Event(
      new GA4FeatureUseEvent({
        ...ga4Props,
        featureClass: FEATURE_CLASS.COPY_CONTENT,
        featureName: FEATURE_NAME.COPY_LAW_CONTENT,
        resourceId: `${topic?.topicType}:${topic?.topicId}`,
        requiredUserStatus: REQUIRED_USER_STATUS.UNAUTHENTICATED_USER,
      })
    )
    const eventName = isInTopicContext ? 'Topic.PopoverCopyContent' : 'Doc.PopoverCopyContent'
    sendMetrics(eventName, defaultProps)
  }

  const isDocumentTopicEntity = entityType === 'DOCUMENT_TOPIC'

  const hasRelatedSumulas =
    Array.isArray(topic?.relatedSumulasDocuments) && topic.relatedSumulasDocuments.length > 0
  const hasRelatedLegis = Array.isArray(topic?.relatedLegis) && topic.relatedLegis.length > 0

  return (
    <>
      {loadingFallbackQuery ? (
        <Spinner />
      ) : (
        <div className={styles.contentRoot} ref={dispatcherRef}>
          {topic?.lawItemType !== 'EMENTA' && (
            <div>
              <div
                className={classNames({
                  [styles.documentTopicContent]: isDocumentTopicEntity,
                  [styles.documentTopicContentShort]:
                    isDocumentTopicEntity && !isLawTopic && !doctrine,
                })}
              >
                <div
                  onCopy={onCopy}
                  data-testid="law-body"
                  className={classNames({ [styles.lawTopicBody]: isLawTopic })}
                >
                  {topic && (
                    <>
                      {topic.image && (
                        <div className={styles.contentImage}>
                          <Image
                            src={topic.image}
                            alt={topic.title}
                            loading="lazy"
                            width={296}
                            height={152}
                            className={styles.imageExtended}
                          />
                        </div>
                      )}
                      {topic.allowedLawUpperItems
                        .filter((item) => !(item.type === 'EMENTA') && !item.hasToBeCompiled)
                        .map((item, index) => (
                          <BodyText
                            size="lg"
                            key={index}
                            className={classNames({
                              [styles.lawItemRevoked]: item.revoked,
                            })}
                          >
                            <span>{item.description}</span>
                            {item.revoked && (
                              <Caption className={styles.lawItemRevokedFlag} asChild>
                                <span>(revogado)</span>
                              </Caption>
                            )}
                          </BodyText>
                        ))}
                      {topic.description && (
                        <>
                          <BodyText
                            size="lg"
                            ref={topicDescriptionRef}
                            className={classNames({
                              [styles.lawItemRevoked]: topic.isRevoked,
                            })}
                          >
                            <span
                              dangerouslySetInnerHTML={{
                                __html: formatDescription(topic.description),
                              }}
                            />
                            {topic?.isRevoked && (
                              <Caption
                                className={styles.lawItemRevokedFlag}
                                data-testid="law-item-revoked"
                                asChild
                              >
                                <span>(revogado)</span>
                              </Caption>
                            )}
                          </BodyText>
                          {topic.allowedLawSubItems
                            .filter((item) => !(item.type === 'EMENTA') && !item.hasToBeCompiled)
                            .map((item, index) => (
                              <BodyText
                                size="lg"
                                key={index}
                                className={classNames({
                                  [styles.lawItemRevoked]: item.revoked,
                                })}
                              >
                                <span>{item.description}</span>
                                {item.revoked && (
                                  <Caption className={styles.lawItemRevokedFlag} asChild>
                                    <span>(revogado)</span>
                                  </Caption>
                                )}
                              </BodyText>
                            ))}
                        </>
                      )}

                      {!topic.description && topic.referenceDescription && (
                        <BodyText size="lg">{topic.referenceDescription}</BodyText>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <Heading size="sm">
            <Link
              href={topic?.referenceUrl}
              onClick={trackTopicLinkClick}
              target="_blank"
              type="primary"
            >
              {topic?.titleWithNickname}
            </Link>
          </Heading>
          {hasLRSubscription && (
            <div>
              {(hasRelatedSumulas || hasRelatedLegis) && (
                <Separator className={styles.contentSeparator} />
              )}
              {Array.isArray(topic?.relatedSumulasDocuments) &&
                topic.relatedSumulasDocuments.length > 0 && (
                  <div>
                    <Caption
                      data-testid="doc-popover-related-sumulas"
                      className={styles.titleRelatedPureBold}
                    >
                      <strong>Súmulas relacionadas</strong>
                    </Caption>
                    <TruncateText value={2} fade="md" defaultFadeVisible>
                      {topic.relatedSumulasDocuments.map((item) => (
                        <Caption key={item.title} className={styles.pureBold}>
                          {item.title}
                        </Caption>
                      ))}
                    </TruncateText>
                  </div>
                )}
              {hasRelatedSumulas && hasRelatedLegis && (
                <div className={styles.sumulaLegisDivider} />
              )}
              {Array.isArray(topic?.relatedLegis) && topic.relatedLegis.length > 0 && (
                <div>
                  <Caption
                    data-testid="doc-popover-related-legis"
                    className={styles.titleRelatedPureBold}
                  >
                    <strong>{RELATED_LEGIS_COMPONENT_HEADING_COPY}</strong>
                  </Caption>
                  <TruncateText value={2} fade="md" defaultFadeVisible>
                    {topic.relatedLegis.map((item) => (
                      <Caption key={item.subject} className={styles.pureBold}>
                        {item.subject} <i>vide</i>{' '}
                        {transformRelatedLegisTitle(
                          item.relatedLegisTitle,
                          item.prepositionForReference
                        )}
                      </Caption>
                    ))}
                  </TruncateText>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )
}

DocPopoverTopic.query = gql`
  query DocPopoverTopic($tidList: [Int!]!) {
    root {
      topics: topicsByIds(tidList: $tidList) {
        ...UseTopicData_topic
        ...UseDoctrineData_topic
      }
    }
  }

  ${useTopicData.fragments.topic}
  ${useDoctrineData.fragments.topic}
`

DocPopoverTopic.fallbackQuery = gql`
  query DocPopoverTopicByUrl($urls: [String!]!) {
    root {
      topicsByUrls: topicsByUrls(urls: $urls) {
        ...UseTopicData_topic
        ...UseDoctrineData_topic
      }
    }
  }

  ${useTopicData.fragments.topic}
  ${useDoctrineData.fragments.topic}
`
